/**
 * RequestDemo — public marketing-site "Request a Demo" lead-capture page.
 * Posts to /api/demo-requests (no auth) and notifies operator admins by email.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CheckCircle2, ArrowLeft } from "lucide-react";

const HIGHLIGHTS = [
  "AI Operations Copilot",
  "Dispatch Control Room",
  "Compliance & document tracking",
  "Ride Health monitoring",
  "Driver fatigue controls",
  "Corporate accounts & cost centres",
];

export default function RequestDemo() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", role: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@")) {
      toast.error("Please enter your name and a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/demo-requests", form);
      setDone(true);
      toast.success(data.message || "Request received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1f3a] text-white grid lg:grid-cols-2" data-testid="request-demo-page">
      <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
        <Link to="/" className="text-white/60 hover:text-[#FFC107] text-sm flex items-center gap-1.5 mb-8" data-testid="request-demo-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="text-[#5e9bff] tracking-[0.3em] text-xs font-bold">CRYSTAL DISPATCH</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black mt-4 leading-tight">
          See a live dispatch<br />operation in action.
        </h1>
        <p className="text-white/70 mt-5 max-w-md">
          Book a guided demo and explore the full Crystal Dispatch ecosystem —
          populated with realistic data, not an empty trial.
        </p>
        <ul className="mt-7 space-y-2.5">
          {HIGHLIGHTS.map((h) => (
            <li key={h} className="flex items-center gap-2.5 text-sm text-white/85">
              <CheckCircle2 className="w-4 h-4 text-[#5e9bff] flex-shrink-0" /> {h}
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white text-[#0b1f3a] p-8 sm:p-12 lg:p-16 flex flex-col justify-center">
        {done ? (
          <div className="max-w-md" data-testid="request-demo-success">
            <CheckCircle2 className="w-12 h-12 text-[#16A34A]" />
            <h2 className="font-heading text-2xl font-black mt-4">Request received</h2>
            <p className="text-muted-foreground mt-2">
              Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}! Our team will be in touch shortly to arrange your demo.
            </p>
            <Link to="/"><Button className="mt-6" data-testid="request-demo-home-btn">Back to home</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-md w-full space-y-4" data-testid="request-demo-form">
            <div>
              <div className="label-mini text-muted-foreground">REQUEST A DEMO</div>
              <h2 className="font-heading text-2xl font-black">Tell us about you</h2>
            </div>
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" className="mt-1.5" data-testid="request-demo-name" required />
            </div>
            <div>
              <label className="text-sm font-medium">Work email *</label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@company.com" className="mt-1.5" data-testid="request-demo-email" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Company</label>
                <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company / council" className="mt-1.5" data-testid="request-demo-company" />
              </div>
              <div>
                <label className="text-sm font-medium">Phone</label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+44…" className="mt-1.5" data-testid="request-demo-phone" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">I am a…</label>
              <Input value={form.role} onChange={(e) => set("role", e.target.value)} placeholder="Operator, council, corporate, etc." className="mt-1.5" data-testid="request-demo-role" />
            </div>
            <div>
              <label className="text-sm font-medium">Anything specific you’d like to see?</label>
              <textarea
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                rows={3}
                placeholder="e.g. compliance, corporate billing, AI Copilot…"
                className="mt-1.5 w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0b1f3a]/20"
                data-testid="request-demo-message"
              />
            </div>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="request-demo-submit">
              {submitting ? "Sending…" : "Request my demo"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
